import type { RendererProps } from "../core/element-registry"
import { useRenderContext, RenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import { useMemo } from "react"
import { evaluateBlockComputed, evaluateBlockInline } from "../components/cogo-block"
import { ComputeEvaluator } from "../runtime/compute-evaluator"

export function BlockRefRenderer({ element }: RendererProps) {
  const ctx = useRenderContext()
  const { resolveSymbol, blocks } = ctx

  const blockType = element.blockType
  if (!blockType || !blocks?.[blockType]) {
    return (
      <div
        data-block-id={blockType ?? ""}
        data-block-ref-status="unresolved"
        className={element.className}
      />
    )
  }

  const rawBlock = blocks[blockType]
  // Support both CogoBlockDef (root) and BlockDef (ui.root) shapes
  const block = {
    ...rawBlock,
    root: rawBlock.root ?? (rawBlock as any).ui?.root,
    variables: rawBlock.variables ?? (rawBlock as any).state,
  }

  // Handle iteration: iterateOver + itemVar
  if (element.iterateOver) {
    const items = resolveSymbol(element.iterateOver)
    if (!Array.isArray(items)) return null

    return (
      <div
        data-block-id={blockType}
        data-block-iterate="true"
        className={element.className}
      >
        {items.map((item, i) => {
          const rawId = (item as Record<string, unknown>)?.id
          const itemKey = rawId != null ? `${String(rawId)}-${i}` : String(i)
          return (
            <BlockRefItem
              key={itemKey}
              blockRoot={block.root}
              blockType={blockType}
              itemVar={element.itemVar}
              item={item}
              parentCtx={ctx}
            />
          )
        })}
      </div>
    )
  }

  // Single block reference — check for computed variables
  const computedDefs = block.variables?.computed
  if (computedDefs && Object.keys(computedDefs).length > 0) {
    return (
      <div
        data-block-id={blockType}
        className={element.className}
        style={element.className ? undefined : { display: "contents" }}
      >
        <SingleBlockRef block={block} computedDefs={computedDefs} parentCtx={ctx} />
      </div>
    )
  }

  return (
    <div
      data-block-id={blockType}
      className={element.className}
      style={element.className ? undefined : { display: "contents" }}
    >
      <ElementRenderer element={block.root} />
    </div>
  )
}

/**
 * Wraps a single (non-iterating) block-ref with computed variable resolution.
 * Uses the same evaluation logic as BlockWithComputed in cogo-block.tsx.
 */
function SingleBlockRef({ block, computedDefs, parentCtx }: {
  block: { root: RendererProps["element"] }
  computedDefs: Record<string, string | number | boolean>
  parentCtx: ReturnType<typeof useRenderContext>
}) {
  const childCtx = useMemo(() => ({
    ...parentCtx,
    resolveSymbol: (expr: string) => {
      if (typeof expr !== "string") return parentCtx.resolveSymbol(expr)

      // Pure #{...} expression
      const pureMatch = /^#\{([^}]+)\}$/.exec(expr)
      if (pureMatch) {
        const inner = pureMatch[1].trim()

        // computed.xxx → evaluate computed expression
        if (inner.startsWith("computed.")) {
          const propName = inner.slice("computed.".length)
          const expression = computedDefs[propName]
          if (expression != null) {
            return evaluateBlockComputed(expression, parentCtx.resolveSymbol, computedDefs)
          }
        }

        // Inline expression (contains operators)
        const isSimplePath = /^[a-zA-Z_][\w.]*$/.test(inner)
        if (!isSimplePath) {
          return evaluateBlockInline(inner, parentCtx.resolveSymbol, computedDefs)
        }

        return parentCtx.resolveSymbol(expr)
      }

      // Template with mixed #{...} patterns
      if (expr.includes("#{")) {
        return expr.replace(/#\{([^}]+)\}/g, (_, ref) => {
          const r = (ref as string).trim()

          if (r.startsWith("computed.")) {
            const propName = r.slice("computed.".length)
            const expression = computedDefs[propName]
            if (expression != null) {
              const val = evaluateBlockComputed(expression, parentCtx.resolveSymbol, computedDefs)
              return val != null ? String(val) : ""
            }
          }

          const isSimplePath = /^[a-zA-Z_][\w.]*$/.test(r)
          if (!isSimplePath) {
            const val = evaluateBlockInline(r, parentCtx.resolveSymbol, computedDefs)
            return val != null ? String(val) : ""
          }

          const val = parentCtx.resolveSymbol(`#{${ref}}`)
          return val != null ? String(val) : ""
        })
      }

      return parentCtx.resolveSymbol(expr)
    },
  }), [parentCtx, computedDefs])

  return (
    <RenderContext.Provider value={childCtx}>
      <ElementRenderer element={block.root} />
    </RenderContext.Provider>
  )
}

function BlockRefItem({
  blockRoot,
  blockType,
  itemVar,
  item,
  parentCtx,
}: {
  blockRoot: RendererProps["element"]
  blockType: string
  itemVar?: string
  item: unknown
  parentCtx: ReturnType<typeof useRenderContext>
}) {
  const { blocks } = parentCtx
  const block = blocks?.[blockType]

  // Get computed expressions from block's variables.cogo.json
  const computedDefs = block?.variables?.computed

  // Create child context with item variable + computed properties
  const childCtx = useMemo(() => {
    const parentItemContext = parentCtx.itemContext ?? {}
    return {
      ...parentCtx,
      itemContext: { ...parentItemContext, ...(itemVar ? { [itemVar]: item } : {}) },
      resolveSymbol: (expr: string) => {
        if (itemVar && typeof expr === "string") {
          // Pure symbol: #{product.name} or #{computed.hasDiscount}
          const pureMatch = /^#\{([^}]+)\}$/.exec(expr)
          if (pureMatch) {
            const inner = pureMatch[1].trim()
            const isSimplePath = /^[a-zA-Z_][\w.]*$/.test(inner)

            if (isSimplePath) {
              // Simple property access: #{product.name}, #{computed.hasDiscount}
              if (inner.startsWith(`${itemVar}.`)) {
                return getNestedValue(item, inner.slice(itemVar.length + 1))
              }
              if (inner.startsWith("computed.") && computedDefs) {
                const propName = inner.slice("computed.".length)
                const expression = computedDefs[propName]
                if (expression) {
                  return evaluateComputed(expression, itemVar, item)
                }
              }
            } else {
              // Inline expression: #{product.rating != null}, #{!computed.freeShipping}
              return evaluateInlineExpression(inner, itemVar, item, computedDefs)
            }
          }

          // Mixed text template: "-#{computed.discountPercent}%"
          if (expr.includes("#{")) {
            return expr.replace(/#\{([^}]+)\}/g, (_, ref) => {
              const refTrimmed = ref.trim()
              if (refTrimmed.startsWith(`${itemVar}.`)) {
                const val = getNestedValue(item, refTrimmed.slice(itemVar.length + 1))
                return val != null ? String(val) : ""
              }
              if (refTrimmed.startsWith("computed.") && computedDefs) {
                const propName = refTrimmed.slice("computed.".length)
                const expression = computedDefs[propName]
                if (expression) {
                  const val = evaluateComputed(expression, itemVar, item)
                  return val != null ? String(val) : ""
                }
              }
              // Fall back to parent for non-item symbols
              const val = parentCtx.resolveSymbol(`#{${ref}}`)
              return val != null ? String(val) : ""
            })
          }
        }

        // Fall back to parent resolver
        return parentCtx.resolveSymbol(expr)
      },
    }
  }, [parentCtx, itemVar, item, computedDefs])

  return (
    <RenderContext.Provider value={childCtx}>
      <ElementRenderer element={blockRoot} />
    </RenderContext.Provider>
  )
}

function getNestedValue(obj: unknown, path: string): unknown {
  const parts = path.split(".")
  let current: unknown = obj
  for (const part of parts) {
    if (current == null || typeof current !== "object") return undefined
    current = (current as Record<string, unknown>)[part]
  }
  return current
}

/**
 * Evaluate an inline expression found inside #{...} that isn't a simple property path.
 * Handles expressions like "product.rating != null", "!computed.freeShipping",
 * "computed.cartItemCount > 0". Resolves computed and item references before eval.
 * Uses ComputeEvaluator (safe, no new Function()).
 */
function evaluateInlineExpression(
  expression: string,
  itemVar: string,
  item: unknown,
  computedDefs?: Record<string, string | number | boolean>,
): unknown {
  try {
    let processed = expression

    // Step 1: Resolve computed.xxx references by evaluating the computed expression
    if (computedDefs) {
      processed = processed.replace(
        /computed\.([a-zA-Z_]\w*)/g,
        (_, propName) => {
          const computedExpr = computedDefs[propName]
          if (computedExpr) {
            const val = evaluateComputed(computedExpr, itemVar, item)
            if (val === null || val === undefined) return "null"
            if (typeof val === "string") return JSON.stringify(val)
            return String(val)
          }
          return "null"
        },
      )
    }

    // Step 2: Resolve itemVar.xxx bare references into literal values
    processed = processed.replace(
      new RegExp(`(?<![\\w.])${itemVar}\\.([a-zA-Z_][\\w.]*)`, "g"),
      (_, propPath) => {
        const val = getNestedValue(item, propPath)
        if (val === null || val === undefined) return "null"
        if (typeof val === "string") return JSON.stringify(val)
        return String(val)
      },
    )

    // Step 3: Evaluate with ComputeEvaluator
    const nullAdapter = { get: () => undefined, resolve: (t: string) => t }
    const compute = new ComputeEvaluator(nullAdapter)
    return compute.evaluate(processed)
  } catch {
    return undefined
  }
}

/**
 * Evaluate a computed expression from block variables.cogo.json.
 * Expressions can reference item properties via:
 *   - #{itemVar.prop} syntax (wrapped references)
 *   - bare itemVar.prop syntax (unwrapped references)
 * Uses ComputeEvaluator (safe, no new Function()).
 */
function evaluateComputed(
  expression: string | number | boolean,
  itemVar: string,
  item: unknown,
): unknown {
  if (typeof expression !== "string") return expression
  try {
    let processed = expression

    // Step 1: Replace #{itemVar.prop} with actual values
    processed = processed.replace(
      new RegExp(`#\\{${itemVar}\\.([a-zA-Z_][\\w.]*)\\}`, "g"),
      (_, propPath) => {
        const val = getNestedValue(item, propPath)
        if (val === null || val === undefined) return "null"
        if (typeof val === "string") return JSON.stringify(val)
        return String(val)
      },
    )

    // Step 2: Replace bare property references like product.price
    processed = processed.replace(
      new RegExp(`(?<![\\w.#])${itemVar}\\.([a-zA-Z_][\\w.]*)`, "g"),
      (_, propPath) => {
        const val = getNestedValue(item, propPath)
        if (val === null || val === undefined) return "null"
        if (typeof val === "string") return JSON.stringify(val)
        return String(val)
      },
    )

    // Step 3: Evaluate with ComputeEvaluator
    const nullAdapter = { get: () => undefined, resolve: (t: string) => t }
    const compute = new ComputeEvaluator(nullAdapter)
    return compute.evaluate(processed)
  } catch {
    return undefined
  }
}
